import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as pdfjsLib from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import { saveAs } from 'file-saver';
import { PdfFile, PdfContextType, BatchInfo, FileType, OutputFormat } from '../types';
import { flattenPdf, checkPdfForLayers } from '../utils/pdfFlattenUtils';
import { extractTextFromDocx, getFileType } from '../utils/docxUtils';
import { convertToMarkdown } from '../utils/markdownUtils';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.js`;

const PdfContext = createContext<PdfContextType | undefined>(undefined);

export const PdfProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<PdfFile[]>([]);
  const [currentFileId, setCurrentFileId] = useState<string | null>(null);
  const [isProcessingBatch, setIsProcessingBatch] = useState<boolean>(false);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('text');

  const addFile = useCallback(async (file: File) => {
    const id = uuidv4();
    const fileType = getFileType(file);
    
    // Validate file type
    if (fileType === 'unknown') {
      throw new Error('Unsupported file type. Please upload a PDF, Word (.docx) or Excel (.xlsx) document.');
    }
    
    setFiles(prevFiles => [...prevFiles, { 
      id, 
      file,
      fileType: fileType as FileType,
      text: '', 
      isProcessing: false, 
      selected: false,
      flattenPdf: false,
      flattenProgress: 0,
      flattenProcessing: false,
      hasLayers: false
    }]);
    setCurrentFileId(id);
    
    // Only check PDF-specific features for PDF files
    if (fileType === 'pdf') {
      try {
        // Check if the PDF might have layers that could be flattened
        const hasLayers = await checkPdfForLayers(file);
        if (hasLayers) {
          setFiles(prevFiles => prevFiles.map(f => 
            f.id === id ? { ...f, hasLayers: true } : f
          ));
        }
      } catch (error) {
        console.warn('Error checking PDF features:', error);
      }
    }
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prevFiles) => {
      const remainingFiles = prevFiles.filter((file) => file.id !== id);
      // Update currentFileId if the removed file was selected
      setCurrentFileId((currentId) => {
        if (currentId === id) {
          return remainingFiles.length > 0 ? remainingFiles[0].id : null;
        }
        return currentId;
      });
      return remainingFiles;
    });
  }, []);

  const updateFileText = useCallback((id: string, text: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === id ? { ...file, text } : file
      )
    );
  }, []);

  const updateFileMarkdown = useCallback((id: string, markdown: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === id ? { ...file, markdown } : file
      )
    );
  }, []);

  const setProcessing = useCallback((id: string, isProcessing: boolean) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === id ? { ...file, isProcessing } : file
      )
    );
  }, []);

  const setError = useCallback((id: string, error: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === id ? { ...file, error } : file
      )
    );
  }, []);

  const toggleFileSelection = useCallback((id: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === id ? { ...file, selected: !file.selected } : file
      )
    );
  }, []);

  const selectAllFiles = useCallback(() => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => ({ ...file, selected: true }))
    );
  }, []);

  const deselectAllFiles = useCallback(() => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => ({ ...file, selected: false }))
    );
    setBatchInfo(null);
  }, []);

  const extractTextFromPdf = useCallback(async (file: File): Promise<string> => {
    try {
      // Load the PDF file
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      
      // Extract text from each page
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .filter((item): item is TextItem => 'str' in item)
          .map((item) => item.str)
          .join(' ');
        
        fullText += pageText + '\n\n';
      }
      
      // Check if we got any text
      if (!fullText.trim()) {
        throw new Error('No text could be extracted. The PDF may be scanned or image-based.');
      }
      
      return fullText;
    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      // Provide user-friendly error messages
      if (error instanceof Error) {
        if (error.message.includes('Invalid PDF')) {
          throw new Error('The file appears to be corrupted or is not a valid PDF.');
        }
        if (error.message.includes('password')) {
          throw new Error('This PDF is password-protected. Please remove the password and try again.');
        }
        throw error;
      }
      throw new Error('Failed to extract text from PDF. Please try a different file.');
    }
  }, []);

  const extractTextFromDocument = useCallback(async (file: File, fileType: FileType, onProgress?: (progress: number) => void): Promise<string> => {
    try {
      if (fileType === 'pdf') {
        return await extractTextFromPdf(file);
      } else if (fileType === 'docx') {
        return await extractTextFromDocx(file, onProgress);
      } else {
        throw new Error(`Unsupported file type: ${fileType}`);
      }
    } catch (error) {
      console.error(`Error extracting text from ${fileType}:`, error);
      throw error;
    }
  }, [extractTextFromPdf]);

  const processSelectedFiles = async () => {
    const selectedFiles = files.filter(file => file.selected);
    if (selectedFiles.length === 0) return;

    setIsProcessingBatch(true);
    
    for (const file of selectedFiles) {
      // Set current file to update UI
      setCurrentFileId(file.id);
      
      // Process the file if it hasn't been processed yet
      if (!file.text || (outputFormat === 'markdown' && !file.markdown)) {
        try {
          setProcessing(file.id, true);

          if (!file.text) {
            const text = await extractTextFromDocument(file.file, file.fileType);
            updateFileText(file.id, text);
          }

          // Also produce Markdown when the user is working in Markdown mode.
          if (outputFormat === 'markdown' && !file.markdown) {
            const markdown = await convertToMarkdown(file.file, file.fileType);
            updateFileMarkdown(file.id, markdown);
          }
        } catch (error) {
          setError(file.id, error instanceof Error ? error.message : 'Unknown error');
        } finally {
          setProcessing(file.id, false);
        }
      }
    }
    
    // Update batch info after processing
    updateBatchInfo();
    setIsProcessingBatch(false);
  };

  const getSelectedFilesText = () => {
    return files
      .filter(file => file.selected && file.text)
      .map(file => `--- ${file.file.name} ---\n\n${file.text}`)
      .join('\n\n');
  };

  // Lazily convert a file to Markdown, caching the result on the file.
  const ensureMarkdown = async (id: string): Promise<string> => {
    const file = files.find(f => f.id === id);
    if (!file) return '';
    if (file.markdown) return file.markdown;

    try {
      setProcessing(id, true);
      const markdown = await convertToMarkdown(file.file, file.fileType);
      updateFileMarkdown(id, markdown);
      return markdown;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to convert to Markdown';
      setError(id, message);
      throw error;
    } finally {
      setProcessing(id, false);
    }
  };

  const getSelectedFilesMarkdown = () => {
    return files
      .filter(file => file.selected && file.markdown)
      .map(file => `# ${file.file.name}\n\n${file.markdown}`)
      .join('\n\n---\n\n');
  };

  // Process a single file - extracts text from PDF or DOCX.
  // Bails out if the file already has text, is processing, OR previously
  // errored — the last guard prevents an infinite retry loop when extraction
  // fails (e.g. scanned/image-only PDFs with no extractable text).
  const processFile = async (id: string): Promise<void> => {
    const file = files.find(f => f.id === id);
    if (!file || file.text || file.isProcessing || file.error) return;
    
    try {
      setProcessing(id, true);
      const text = await extractTextFromDocument(file.file, file.fileType);
      updateFileText(id, text);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to extract text';
      setError(id, errorMessage);
    } finally {
      setProcessing(id, false);
    }
  };
  
  const toggleFlatten = useCallback((id: string) => {
    setFiles(prevFiles => prevFiles.map(file => 
      file.id === id ? { ...file, flattenPdf: !file.flattenPdf } : file
    ));
  }, []);
  
  const updateFlattenProgress = useCallback((id: string, progress: number) => {
    setFiles(prevFiles => prevFiles.map(file => 
      file.id === id ? { ...file, flattenProgress: progress } : file
    ));
  }, []);
  
  const setFlattenProcessing = useCallback((id: string, isProcessing: boolean) => {
    setFiles(prevFiles => prevFiles.map(file => 
      file.id === id ? { ...file, flattenProcessing: isProcessing } : file
    ));
  }, []);
  
  const setFlattenedPdfBlob = useCallback((id: string, blob: Blob) => {
    setFiles(prevFiles => prevFiles.map(file => 
      file.id === id ? { ...file, flattenedPdfBlob: blob } : file
    ));
  }, []);
  
  const checkForLayers = async (id: string): Promise<boolean> => {
    const file = files.find(f => f.id === id);
    if (!file) return false;
    
    try {
      const hasLayers = await checkPdfForLayers(file.file);
      return hasLayers;
    } catch (error) {
      console.error('Error checking for layers:', error);
      return false;
    }
  };
  
  const processWithFlatten = async (id: string): Promise<boolean> => {
    const file = files.find(f => f.id === id);
    if (!file) return false;
    
    try {
      setFlattenProcessing(id, true);
      updateFlattenProgress(id, 0);
      
      const flattenedPdfBlob = await flattenPdf(file.file, (progress) => {
        updateFlattenProgress(id, progress);
      });
      
      setFlattenedPdfBlob(id, flattenedPdfBlob);
      return true;
    } catch (error) {
      setError(id, error instanceof Error ? error.message : 'PDF flattening failed');
      return false;
    } finally {
      setFlattenProcessing(id, false);
    }
  };
  
  const downloadFlattenedPdf = useCallback((id: string) => {
    const file = files.find(f => f.id === id);
    if (!file || !file.flattenedPdfBlob) return;
    
    try {
      // Create a filename for the flattened PDF
      const originalName = file.file.name;
      const flattenedName = originalName.replace(/\.pdf$/i, '-flattened.pdf');
      
      // Download the flattened PDF
      saveAs(file.flattenedPdfBlob, flattenedName);
    } catch (error) {
      console.error('Error downloading flattened PDF:', error);
      setError(id, 'Failed to download flattened PDF');
    }
  }, [files, setError]);
  
  const updateBatchInfo = () => {
    const selectedFiles = files.filter(file => file.selected);
    
    if (selectedFiles.length <= 1) {
      setBatchInfo(null);
      return;
    }
    
    // Get processed files with text
    const processedFiles = selectedFiles.filter(file => file.text && !file.isProcessing);
    
    // Calculate total pages (estimated based on text length if not available)
    const totalPages = processedFiles.reduce((total, file) => {
      // Estimate page count based on text length if not explicitly set
      const estimatedPages = Math.max(1, Math.ceil(file.text.length / 3000));
      return total + estimatedPages;
    }, 0);
    
    // Create a combined filename string
    const fileNames = selectedFiles.map(file => file.file.name);
    let combinedName = '';
    
    if (fileNames.length <= 3) {
      combinedName = fileNames.join(', ');
    } else {
      combinedName = `${fileNames[0]}, ${fileNames[1]} and ${fileNames.length - 2} more files`;
    }
    
    // Create combined text
    const combinedText = getSelectedFilesText();
    
    setBatchInfo({
      combinedName,
      totalFiles: selectedFiles.length,
      processedFiles: processedFiles.length,
      totalPages,
      combinedText
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contextValue = useMemo(() => ({
    files,
    addFile,
    removeFile,
    setError,
    currentFileId,
    setCurrentFileId,
    toggleFileSelection,
    selectAllFiles,
    deselectAllFiles,
    processSelectedFiles,
    getSelectedFilesText,
    isProcessingBatch,
    batchInfo,
    updateBatchInfo,
    // File processing
    processFile,
    // Markdown output
    outputFormat,
    setOutputFormat,
    ensureMarkdown,
    getSelectedFilesMarkdown,
    // PDF flattening functions
    toggleFlatten,
    processWithFlatten,
    updateFlattenProgress,
    setFlattenProcessing,
    checkForLayers,
    downloadFlattenedPdf,
    setFlattenedPdfBlob
  }), [
    files,
    addFile,
    removeFile,
    setError,
    currentFileId,
    toggleFileSelection,
    selectAllFiles,
    deselectAllFiles,
    isProcessingBatch,
    batchInfo,
    outputFormat,
    toggleFlatten,
    updateFlattenProgress,
    setFlattenProcessing,
    setFlattenedPdfBlob,
    downloadFlattenedPdf
  ]);

  return (
    <PdfContext.Provider value={contextValue}>
      {children}
    </PdfContext.Provider>
  );
};

export const usePdf = (): PdfContextType => {
  const context = useContext(PdfContext);
  if (context === undefined) {
    throw new Error('usePdf must be used within a PdfProvider');
  }
  return context;
};
